/**
 * History in and out on the profile (`D201`): `edit=import` and
 * `edit=export-csv`, and the card in the account section that opens them.
 *
 * ── THE IMPORT MATCHES AGAINST THE DEVICE'S CATALOGUE COPY ─────────────────
 * The names are matched in English — what Strong and Hevy write — and in the
 * app's language, against the copy `D194` already keeps on the device; the
 * sheet refreshes it once on opening, so a person importing on their first
 * day has one. No name leaves the device.
 *
 * ── THE EXPORT IS STREAMED BY THE SERVER, SHARED BY THE DEVICE ─────────────
 * `GET /me/export.csv` streams; the app holds the text once and hands it to
 * the share sheet as a file, or downloads it on a browser without one.
 */
import {
  fetchExportCsv,
  importHistoryMutation,
} from "@repo/client/data/workout";
import type { CatalogueLocale } from "@repo/contract/catalog";
import {
  "history-transfer.export_action" as exportAction,
  "history-transfer.export_busy" as exportBusy,
  "history-transfer.export_done" as exportDone,
  "history-transfer.export_failed" as exportFailed,
  "history-transfer.export_helper" as exportHelper,
  "history-transfer.export_title" as exportTitle,
  "history-transfer.eyebrow" as eyebrowText,
  "history-transfer.import_title" as importTitle,
  "history-transfer.row_export" as rowExport,
  "history-transfer.row_export_hint" as rowExportHint,
  "history-transfer.row_import" as rowImport,
  "history-transfer.row_import_hint" as rowImportHint,
} from "@repo/i18n";
import { getLocale } from "@repo/i18n/runtime";
import { FileDown, FileUp } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useApi } from "../../../data/api";
import {
  refreshCatalogueSnapshot,
  useCatalogueSnapshot,
} from "../../../data/catalogue-snapshot";
import { useDataMutation } from "../../../data/query-client";
import { shareFile } from "../../../native";
import { Button, Icon } from "../../../ui/controls";
import type { EditorHost } from "../editors/editor-shell";
import { EditorShell } from "../editors/editor-shell";
import type { EditorId } from "../editors/ids";
import { FieldError, Helper } from "../editors/parts";
import { Card } from "../sections/parts";
import { ImportFlow } from "./import-flow";
import type { CatalogueName } from "./match";

const appCatalogueLocale = (): CatalogueLocale =>
  getLocale().startsWith("pt") ? "pt" : "en";

const systemNow = () => new Date();

/** `edit=import`. */
export function ImportEditor({ host }: { readonly host: EditorHost }) {
  const api = useApi();
  const locale = appCatalogueLocale();
  const english = useCatalogueSnapshot("en");
  const own = useCatalogueSnapshot(locale);
  useEffect(() => {
    refreshCatalogueSnapshot(api, "en").catch(() => undefined);
    if (locale !== "en") {
      refreshCatalogueSnapshot(api, locale).catch(() => undefined);
    }
  }, [api, locale]);
  const catalogue = useMemo((): readonly CatalogueName[] | undefined => {
    if (english === undefined || own === undefined) {
      return undefined;
    }
    const names = english.map((item) => ({ id: item.id, name: item.name }));
    return locale === "en"
      ? names
      : [...names, ...own.map((item) => ({ id: item.id, name: item.name }))];
  }, [english, locale, own]);
  const shown = useMemo(
    () => new Map((own ?? []).map((item) => [item.id, item.name])),
    [own]
  );
  const displayName = useCallback(
    (exerciseId: string) => shown.get(exerciseId) ?? exerciseId,
    [shown]
  );
  const { mutateAsync } = useDataMutation(importHistoryMutation(api));
  const { onClose, onSaved } = host;
  const done = useCallback(() => {
    onSaved({});
    onClose();
  }, [onClose, onSaved]);
  return (
    <EditorShell
      eyebrow={eyebrowText()}
      footer={false}
      host={host}
      title={importTitle()}
    >
      <ImportFlow
        catalogue={catalogue}
        displayName={displayName}
        now={systemNow}
        onDone={done}
        send={mutateAsync}
      />
    </EditorShell>
  );
}

type ExportState = "busy" | "done" | "failed" | "idle";

/** `edit=export-csv`. */
export function ExportCsvEditor({ host }: { readonly host: EditorHost }) {
  const api = useApi();
  const [state, setState] = useState<ExportState>("idle");
  const run = useCallback(() => {
    setState("busy");
    fetchExportCsv(api, { locale: "en" })
      .then((text) =>
        shareFile({
          blob: new Blob([text], { type: "text/csv" }),
          filename: `mypocketgym-${new Date().toISOString().slice(0, 10)}.csv`,
          title: exportTitle(),
        })
      )
      .then(
        (outcome) => setState(outcome === "failed" ? "failed" : "done"),
        () => setState("failed")
      );
  }, [api]);
  let label = exportAction();
  if (state === "busy") {
    label = exportBusy();
  } else if (state === "done") {
    label = exportDone();
  }
  return (
    <EditorShell
      eyebrow={eyebrowText()}
      footer={
        <Button
          className="w-full"
          data-testid="export-csv"
          disabled={state === "busy"}
          icon={FileDown}
          onClick={run}
          size="lg"
        >
          {label}
        </Button>
      }
      host={host}
      title={exportTitle()}
    >
      <Helper>{exportHelper()}</Helper>
      {state === "failed" ? <FieldError>{exportFailed()}</FieldError> : null}
    </EditorShell>
  );
}

const ROW =
  "flex min-h-16 w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-canvas active:bg-wash";

function TransferRow(props: {
  readonly edit: EditorId;
  readonly first: boolean;
  readonly hint: string;
  readonly icon: typeof FileUp;
  readonly label: string;
  readonly open: (id: EditorId) => void;
}) {
  const { edit, open } = props;
  const onClick = useCallback(() => open(edit), [edit, open]);
  return (
    <button
      className={props.first ? ROW : `${ROW} border-line border-t`}
      data-testid={`row-${edit}`}
      onClick={onClick}
      type="button"
    >
      <span className="shrink-0 text-ink-2">
        <Icon icon={props.icon} size={20} />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block font-semibold text-base text-ink">
          {props.label}
        </span>
        <span className="block text-ink-3 text-sm">{props.hint}</span>
      </span>
    </button>
  );
}

/** The account section's two doors: import and export. */
export function HistoryTransferCard(props: {
  readonly open: (id: EditorId) => void;
}) {
  return (
    <Card className="mt-3">
      <TransferRow
        edit="import"
        first
        hint={rowImportHint()}
        icon={FileUp}
        label={rowImport()}
        open={props.open}
      />
      <TransferRow
        edit="export-csv"
        first={false}
        hint={rowExportHint()}
        icon={FileDown}
        label={rowExport()}
        open={props.open}
      />
    </Card>
  );
}
